/*
  Automatic openings and the cart (bb-overlay-coordinator.js):
  - never on the cart page;
  - elsewhere, if the shopper is using the cart (drawer, cart tools, variant
    editor, coupon overlay, summary sheet, checkout starting) when the
    configured delay ends, the pop-up waits and opens a few seconds after the
    cart is closed — the wait starts again if the cart is opened meanwhile;
  - if the cart opens while the pop-up is showing, it steps aside at once and
    comes back the same way.
  Waiting or stepping aside never counts as a dismissal: only the shopper
  closing the pop-up records one, under the merchant's own rules.
*/
const PROMOTION_POPUP_QUIET_MS = 3000;

class PromotionPopup extends HTMLElement {
  constructor() {
    super();

    this.expiresDays = this.getAttribute('data-expire') || 1;
    this.timeToShow = parseInt(this.getAttribute('data-delay')) || 0;
    this._storage = this.getBestStorage();

    const popupId = this.id || 'global';
    this.dismissKey = `_halo_promotion_popup_dismiss_${popupId}`;

    this.overlay = this.querySelector('.drawer__overlay') || this.querySelector('[id^="Drawer-Overlay-"]');
    this.content = this.querySelector('.popup__inner');

    this.querySelector('.popup__close')?.addEventListener('click', this.dismiss.bind(this));
    this.overlay?.addEventListener('click', this.dismiss.bind(this));

    if (Shopify.designMode) {
      document.addEventListener('shopify:section:select', (e) => {
        if (!e?.target?.matches?.('.section-promotion-popup')) return;
        const selectedSectionId = e?.detail?.sectionId;
        const mySectionId = (this.id || '').replace('Promotion-popup-', '');
        if (selectedSectionId && mySectionId && selectedSectionId !== mySectionId) return;
        this.setOpenPopup(0);
      });

      document.addEventListener('shopify:section:deselect', (e) => {
        if (!e?.target?.matches?.('.section-promotion-popup')) return;
        const deselectedSectionId = e?.detail?.sectionId;
        const mySectionId = (this.id || '').replace('Promotion-popup-', '');
        if (deselectedSectionId && mySectionId && deselectedSectionId !== mySectionId) return;
        this.close({ persist: false });
      });
    }
  }

  connectedCallback() {
    try { this.load(); } catch (_) { this.setOpenPopup(this.timeToShow); }
  }

  load() {
    this.observeEmailSuccess();

    const hasSuccess = this.querySelector('.email-signup__message--success');
    const hasMessage = this.querySelector('.email-signup__message');

    if (hasSuccess) {
      if (hasMessage) this.classList.add('active');
      // The sign-up confirmation after the form posted: shown at once, or
      // once the cart is closed.
      if (!Shopify.designMode) this.openWhenCartIdle({ confirmation: true });
      return;
    }

    if (this.getStore(this.dismissKey)) return;

    if (hasMessage) this.classList.add('active');
    if (!Shopify.designMode) this.scheduleAutoOpen(this.timeToShow);
  }

  isCartPage() {
    return document.body.classList.contains('template-cart');
  }

  scheduleAutoOpen(delay) {
    if (this.isCartPage()) return;
    clearTimeout(this._autoTimer);
    this._autoTimer = setTimeout(() => this.openWhenCartIdle(), delay);
  }

  openWhenCartIdle({ confirmation = false } = {}) {
    if (this.hasAttribute('open')) return;
    if (!confirmation && (this.isCartPage() || this.getStore(this.dismissKey))) return;
    const overlay = window.BBOverlay;
    if (!overlay || !overlay.cartBusy()) {
      this.setOpenPopup(0);
      return;
    }
    if (this._cancelWait) this._cancelWait();
    this._cancelWait = overlay.whenCartIdle(() => {
      this._cancelWait = null;
      if (this.hasAttribute('open')) return;
      if (!confirmation && (this.isCartPage() || this.getStore(this.dismissKey))) return;
      this.setOpenPopup(0);
    }, PROMOTION_POPUP_QUIET_MS);
  }

  // The cart opened over the pop-up (an add to cart finishing, say): it
  // steps aside at once — no dismissal recorded, its focus trap released
  // without pulling focus back — and returns once the cart is closed.
  yieldToCart() {
    if (!this.hasAttribute('open')) return;
    this.stopWatchingCart();
    removeTrapFocus();
    this.removeAttribute('open');
    this.unlockPage();
    this.openWhenCartIdle({ confirmation: !!this.querySelector('.email-signup__message--success') });
  }

  watchForCart() {
    if (Shopify.designMode || !window.BBOverlay) return;
    this.stopWatchingCart();
    this._onOverlayChange = (event) => {
      const detail = event.detail || {};
      if (detail.open && String(detail.id).indexOf('cart:') === 0) this.yieldToCart();
    };
    document.addEventListener('bb:overlay:change', this._onOverlayChange);
    // Cart surfaces that only show their state in the page.
    this._cartPoll = setInterval(() => {
      if (window.BBOverlay.cartBusy()) this.yieldToCart();
    }, 300);
  }

  stopWatchingCart() {
    if (this._onOverlayChange) document.removeEventListener('bb:overlay:change', this._onOverlayChange);
    this._onOverlayChange = null;
    clearInterval(this._cartPoll);
    this._cartPoll = null;
  }

  // The page lock is shared with the cart: closing this pop-up never
  // unlocks the page under an open cart (and the reverse).
  lockPage() {
    if (window.BBOverlay) {
      window.BBOverlay.lock(`promotion:${this.id || 'popup'}`, this);
      return;
    }
    document.body.classList.add('overflow-hidden');
    document.documentElement.setAttribute('scroll-lock', '');
  }

  unlockPage() {
    if (window.BBOverlay) {
      window.BBOverlay.unlock(`promotion:${this.id || 'popup'}`);
      return;
    }
    document.body.classList.remove('overflow-hidden');
    document.documentElement.removeAttribute('scroll-lock');
  }

  observeEmailSuccess() {
    const target = this.querySelector('form') || this;
    this.successObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.classList.contains('email-signup__message--success')) {
            this.classList.add('active');
            this.setOpenPopup(0);
          }
        });
      });
    });
    this.successObserver.observe(target, { childList: true, subtree: true });
  }

  setOpenPopup(timeToShow) {
    setTimeout(() => {
      if (this._cancelWait) this._cancelWait();
      this._cancelWait = null;
      this.activeElement = document.activeElement;
      this.setAttribute('open', 'true');
      this.animateOpen();
      this.lockPage();

      this.addEventListener('transitionend', () => {
        // It may have stepped aside for the cart since.
        if (!this.hasAttribute('open')) return;
        const containerToTrapFocusOn = this.content;
        const focusElement = this.querySelector('.field__input');
        trapFocus(containerToTrapFocusOn, focusElement);
      }, { once: true });
      this.watchForCart();
    }, timeToShow);
  }

  close({ persist } = { persist: true }) {
    if (persist) this.setStoreDays(this.dismissKey, '1', this.expiresDays);
    this.stopWatchingCart();
    if (this._cancelWait) this._cancelWait();
    this._cancelWait = null;
    this.animateClose();
    setTimeout(() => {
      this.removeAttribute('open');
      this.unlockPage();
      removeTrapFocus(this.activeElement);
    }, 100);
  }

  animateOpen() {
    Motion.timeline([
      [this.content, { opacity: [0, 1], transform: ['translate(-50%, -40%)', 'translate(-50%, -50%)'] }, { duration: 0.3, at: '-0.15' }]
    ]).finished;
  }

  animateClose() {
    Motion.timeline([
      [this.content, { opacity: [1, 0], transform: ['translate(-50%, -50%)', 'translate(-50%, -40%)'] }, { duration: 0.2 }]
    ]).finished;
  }

  tempClose() {
    this.stopWatchingCart();
    this.animateClose();
    setTimeout(() => {
      this.removeAttribute('open');
      this.unlockPage();
      removeTrapFocus(this.activeElement);
    }, 100);
  }

  dismiss() {
    this.close({ persist: true });
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  setStoreDays(name, value, days) {
    var d = new Date();
    d.setTime(d.getTime() + 24 * 60 * 60 * 1000 * days);
    this.setStoreUntil(name, value, d);
  }

  setStoreUntil(name, value, date) {
    try {
      if (!this._storage) return;
      var payload = { v: value, exp: date.getTime() };
      this._storage.setItem(name, JSON.stringify(payload));
    } catch (_) {}
  }

  getStore(name) {
    try {
      if (!this._storage) return null;
      var raw = this._storage.getItem(name);
      if (!raw) return null;
      var payload = JSON.parse(raw);
      if (!payload || typeof payload.exp !== 'number') return null;
      if (Date.now() > payload.exp) {
        this._storage.removeItem(name);
        return null;
      }
      return payload.v;
    } catch (_) { return null; }
  }

  getBestStorage() {
    try {
      var k = '__pp_test__';
      window.localStorage.setItem(k, '1');
      window.localStorage.removeItem(k);
      return window.localStorage;
    } catch (_) {}
    try {
      var k2 = '__pp_test__';
      window.sessionStorage.setItem(k2, '1');
      window.sessionStorage.removeItem(k2);
      return window.sessionStorage;
    } catch (_) {}
    return null;
  }
}
if (!customElements.get('promotion-popup')) customElements.define('promotion-popup', PromotionPopup);